import { useRef, useState, useCallback, useEffect } from "react"
import { HardDrive, UploadCloud, AlertCircle, AlertTriangle, CheckCircle2, Minimize2, Settings, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { jobsApi, tasksApi, cliModulesApi } from "@/lib/api"
import type { CloudConn, Job } from "@/App"

type Source = 'drive' | 'dropbox' | 'local'

const sources: { id: Source; label: string; desc: string; icon: React.ReactNode }[] = [
  {
    id: 'drive',
    label: 'Google Drive',
    desc: 'Import files directly from your Drive',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
        <path d="M4.5 19L8.5 12L4 5H12L16 12L12 19H4.5Z" fill="#0F9D58"/>
        <path d="M16 12L20 19H8L12 12H16Z" fill="#4285F4"/>
        <path d="M8.5 12L4 5H16L20 12H8.5Z" fill="#FFBB00"/>
      </svg>
    ),
  },
  {
    id: 'dropbox',
    label: 'Dropbox',
    desc: 'Import files from your Dropbox account',
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="#0061FF">
        <path d="M6 2L1 5.5l5 3.5 5-3.5L6 2zM16 2l-5 3.5 5 3.5 5-3.5L16 2zM1 12.5L6 16l5-3.5L6 9l-5 3.5zM16 9l-5 3.5 5 3.5 5-3.5L16 9zM6 17.5l5 3.5 5-3.5-5-3.5-5 3.5z"/>
      </svg>
    ),
  },
  {
    id: 'local',
    label: 'Local',
    desc: 'Upload files directly from your computer',
    icon: <HardDrive className="w-7 h-7 text-muted-foreground" />,
  },
]

async function collectFilesFromDrop(dt: DataTransfer): Promise<File[]> {
  const files: File[] = []
  async function traverseEntry(entry: FileSystemEntry): Promise<void> {
    if (entry.isFile) {
      await new Promise<void>(res => { (entry as FileSystemFileEntry).file(f => { files.push(f); res() }) })
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      await new Promise<void>(res => {
        reader.readEntries(async entries => { for (const e of entries) await traverseEntry(e); res() })
      })
    }
  }
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry()
    if (entry) await traverseEntry(entry)
  }
  return files
}

function NotDivisibleHint({ total, perProduct, remainder }: { total: number; perProduct: number; remainder: number }) {
  const toAdd = perProduct - remainder
  const toRemove = remainder
  const nextUp = total + toAdd
  const nextDown = total - toRemove
  return (
    <div className="flex flex-col gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
      <div className="flex items-center gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span><strong>{total} images</strong> is not divisible by {perProduct}</span>
      </div>
      <div className="flex gap-3 pl-6 text-xs font-medium flex-wrap">
        <span className="px-2 py-1 rounded bg-destructive/10 border border-destructive/20">
          + {toAdd} image{toAdd !== 1 ? 's' : ''} → {nextUp} total ({nextUp / perProduct} products)
        </span>
        <span className="px-2 py-1 rounded bg-destructive/10 border border-destructive/20">
          − {toRemove} image{toRemove !== 1 ? 's' : ''} → {nextDown} total ({nextDown / perProduct} products)
        </span>
      </div>
    </div>
  )
}

type Props = {
  onBackground?: (progress: number) => void
  cloud?: CloudConn
  onGoSettings?: () => void
  onJobCreated?: (job: Job) => void
  onConfigure?: (jobId: string) => void
}

const STEPS = ['Source', 'Setup', 'Upload', 'Configure']

export function NewBatchPage({ onBackground, cloud, onGoSettings, onJobCreated, onConfigure }: Props) {
  const [step, setStep]         = useState(1)
  const [maxStep, setMaxStep]   = useState(1)
  const [source, setSource]     = useState<Source | null>('local')
  const [driveEnabled, setDriveEnabled]     = useState(false)
  const [dropboxEnabled, setDropboxEnabled] = useState(false)

  useEffect(() => {
    cliModulesApi.list().then(mods => {
      const drivemod   = mods.find(m => m.module_id === "storage_drive")
      const dropboxmod = mods.find(m => m.module_id === "storage_dropbox")
      if (drivemod)   setDriveEnabled(drivemod.enabled)
      if (dropboxmod) setDropboxEnabled(dropboxmod.enabled)
    }).catch(() => {})
  }, [])

  const hasCloudSource = driveEnabled || dropboxEnabled
  const [showDropZone, setShowDropZone] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [files, setFiles]       = useState<File[]>([])
  const [sourceUrl, setSourceUrl] = useState('')

  // Step 2 — setup
  const [imgsPerProduct, setImgsPerProduct] = useState(8)

  // Step 3 — upload/pull
  const [uploading, setUploading]   = useState(false)
  const [uploadPct, setUploadPct]   = useState(0)
  const [background, setBackground] = useState(false)
  const [createdJobId, setCreatedJobId] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [pulledCount, setPulledCount] = useState<number | null>(null)
  const [pullingFile, setPullingFile] = useState<string | null>(null)

  const uploadSucceededRef = useRef(false)

  // Clean up orphaned job if user navigates away before upload completes
  useEffect(() => {
    uploadSucceededRef.current = false
    return () => {
      if (createdJobId !== null && !uploadSucceededRef.current) {
        jobsApi.remove(createdJobId).catch(() => {})
      }
    }
  }, [createdJobId])

  const [collectingWhich, setCollectingWhich] = useState<'images' | 'folder' | null>(null)
  const collectingRef = useRef(false)

  const imgRef    = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement>(null)

  const cancelCollecting = useCallback(() => { setCollectingWhich(null); collectingRef.current = false }, [])

  useEffect(() => {
    const imgs = imgRef.current
    const fol = folderRef.current
    imgs?.addEventListener('cancel', cancelCollecting)
    fol?.addEventListener('cancel', cancelCollecting)
    return () => { imgs?.removeEventListener('cancel', cancelCollecting); fol?.removeEventListener('cancel', cancelCollecting) }
  }, [cancelCollecting])

  const pickFiles = (which: 'images' | 'folder') => {
    setCollectingWhich(which)
    collectingRef.current = true
    setTimeout(() => (which === 'images' ? imgRef : folderRef).current?.click(), 50)
  }

  const goToStep = (n: number) => { setStep(n); setMaxStep(m => Math.max(m, n)) }

  const imageFiles = files.filter(f => f.type.startsWith('image/') && f.size > 0)
  const totalImages = imageFiles.length
  const totalSize = imageFiles.reduce((s, f) => s + f.size, 0)
  const totalSizeLabel = totalSize >= 1_073_741_824
    ? `${(totalSize / 1_073_741_824).toFixed(2)} GB`
    : totalSize >= 1_048_576
      ? `${(totalSize / 1_048_576).toFixed(1)} MB`
      : `${(totalSize / 1024).toFixed(0)} KB`
  const safeImgsPerProduct = imgsPerProduct > 0 ? imgsPerProduct : 1
  const remainder = totalImages % safeImgsPerProduct
  const divisible = totalImages > 0 && remainder === 0
  const estimatedProducts = divisible ? totalImages / safeImgsPerProduct : null

  const deriveJobName = () => {
    const now = new Date()
    const date = now.toISOString().slice(0, 10)
    const time = now.toTimeString().slice(0, 5).replace(':', '-')
    if (sourceUrl) {
      // Strip query params and use last path segment, fallback to "Batch"
      try {
        const u = new URL(sourceUrl)
        const seg = u.pathname.split('/').filter(Boolean).pop() ?? ''
        const label = (seg && seg !== 'open') ? seg : 'Batch'
        return `${label}_${date}_${time}`
      } catch { return `Batch_${date}_${time}` }
    }
    return `Batch_${date}_${time}`
  }

  const handleFilesCollected = useCallback((collected: File[]) => {
    setCollectingWhich(null)
    collectingRef.current = false
    if (!collected.length) return
    setFiles(collected)
    setUploadPct(0)
    setCreatedJobId(null)
    setUploadError(null)
    // Stay on step 1 — show confirmation before moving on
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSource = (id: Source) => {
    setSource(id)
    if (id !== 'local') { setShowDropZone(false); setSourceUrl(''); return }
    setShowDropZone(true)
  }

  const handleUrlNext = () => {
    if (!sourceUrl.trim()) return
    goToStep(2)
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    setCollectingWhich('folder')
    collectingRef.current = true
    handleFilesCollected(await collectFilesFromDrop(e.dataTransfer))
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) handleFilesCollected(Array.from(e.target.files))
    else { setCollectingWhich(null); collectingRef.current = false }
    e.target.value = ''
  }

  const startUpload = async () => {
    goToStep(3)
    setUploadPct(0)
    setUploading(true)
    setUploadError(null)

    let createdId: number | null = null
    try {
      const dto = await jobsApi.create({
        name: deriveJobName(),
        source: source ?? 'local',
        source_url: sourceUrl || null,
        file_count: totalImages,
        sku_count: estimatedProducts ?? 0,
      })
      createdId = dto.id
      setCreatedJobId(dto.id)

      if (source === 'local' && imageFiles.length > 0) {
        const CHUNK = 5
        let sent = 0
        while (sent < imageFiles.length) {
          const chunk = imageFiles.slice(sent, sent + CHUNK)
          await jobsApi.uploadFlat(dto.id, chunk)
          sent += chunk.length
          const pct = Math.round((sent / imageFiles.length) * 100)
          setUploadPct(pct)
          onBackground?.(pct)
        }
      } else if (source === 'drive' || source === 'dropbox') {
        const { task_id, total } = await jobsApi.startPull(dto.id)
        if (total > 0) setUploadPct(0)
        await new Promise<void>((resolve, reject) => {
          const tick = async () => {
            try {
              const t = await tasksApi.getStatus(task_id)
              if (t.status === 'not_found') { reject(new Error('Pull task not found')); return }
              if ('done' in t && 'total' in t && t.total > 0) {
                const pct = Math.round((t.done / t.total) * 100)
                setUploadPct(pct); onBackground?.(pct)
                if ('reload_targets' in t) setPullingFile((t.reload_targets as string[])[0] ?? null)
              }
              if (t.status === 'done') {
                const fileCount = (t.result?.file_count as number | undefined) ?? t.done ?? 0
                if (fileCount === 0) { reject(new Error('No images found in this folder. Check the link and try again.')); return }
                try { const updated = await jobsApi.get(dto.id); dto.name = updated.name } catch { /* ignore */ }
                setPulledCount(fileCount); setUploadPct(100); onBackground?.(100); resolve()
              } else if (t.status === 'error') {
                reject(new Error(t.error ?? 'Pull failed'))
              } else {
                setTimeout(tick, 2000)
              }
            } catch (e) {
              reject(e)
            }
          }
          tick()
        })
      } else {
        setUploadPct(100)
        onBackground?.(100)
      }

      await jobsApi.update(dto.id, { action: 'mark_not_configured' })

      const job: Job = {
        id: String(dto.id), name: dto.name, source: dto.source,
        fileCount: dto.file_count, skuCount: dto.sku_count,
        status: 'not_configured', createdAt: dto.created_at,
        sourceUrl: dto.source_url ?? undefined,
        outputFormat: dto.output_format ?? undefined,
        outputResolution: (dto.output_resolution as Job['outputResolution']) ?? undefined,
      }
      uploadSucceededRef.current = true
      onJobCreated?.(job)
      goToStep(4)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
      if (createdId !== null) {
        jobsApi.remove(createdId).catch(() => {})
      }
    }

    setUploading(false)
  }

  if (background && step === 3) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-40 text-muted-foreground">
        <p className="text-sm">Upload running in background — {uploadPct}%</p>
        <Button variant="outline" size="sm" onClick={() => setBackground(false)}>Show progress</Button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-8">

      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {STEPS.map((label, idx) => {
          const n = idx + 1
          const canNav = n !== step && n <= maxStep && !uploading
          return (
            <div key={n} className="flex items-center gap-2">
              <button
                onClick={() => canNav && setStep(n)}
                disabled={!canNav}
                className={[
                  "flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-colors",
                  canNav ? "cursor-pointer hover:opacity-70" : "cursor-default",
                  n <= step ? "bg-foreground text-background" : "bg-secondary text-muted-foreground"
                ].join(" ")}
              >
                {n < step ? "✓" : n}
              </button>
              <button
                onClick={() => canNav && setStep(n)}
                disabled={!canNav}
                className={["text-xs font-medium transition-colors", canNav ? "cursor-pointer hover:text-foreground" : "cursor-default", n === step ? "" : "text-muted-foreground"].join(" ")}
              >
                {label}
              </button>
              {n < STEPS.length && <div className="w-6 h-px bg-border" />}
            </div>
          )
        })}
      </div>

      {/* ── Step 1 — Source ── */}
      {step === 1 && (
        <div className="flex flex-col gap-4">
          {hasCloudSource && (
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Where are your files?</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Choose your source to start the batch</p>
          </div>
          )}

          {hasCloudSource && <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {sources.filter(s => s.id === 'local' || (s.id === 'drive' && driveEnabled) || (s.id === 'dropbox' && dropboxEnabled)).map(s => (
              <button key={s.id} onClick={() => handleSource(s.id)}
                className={["flex flex-col items-start gap-4 p-5 rounded-xl border transition-all text-left group",
                  source === s.id ? "border-foreground/60 bg-secondary/40" : "border-border hover:border-foreground/40 hover:bg-secondary/30"].join(" ")}>
                <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                  {s.icon}
                </div>
                <div>
                  <div className="text-sm font-semibold">{s.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{s.desc}</div>
                </div>
              </button>
            ))}
          </div>}

          {(source === 'drive' || source === 'dropbox') && (() => {
            const isConnected = source === 'drive' ? cloud?.google.connected : cloud?.dropbox.connected
            return (
              <div className="flex flex-col gap-3">
                {!isConnected && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-xs">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Not connected — </span>
                    <button onClick={onGoSettings} className="underline underline-offset-2 font-medium flex items-center gap-1 hover:opacity-80">
                      <Settings className="w-3 h-3" /> Go to Settings
                    </button>
                    <span className="text-destructive/70 ml-1">or paste a public link below</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <input autoFocus value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleUrlNext()}
                    placeholder={source === 'drive' ? 'https://drive.google.com/drive/folders/…' : 'https://www.dropbox.com/sh/…'}
                    className="flex-1 h-9 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-foreground/30" />
                  <Button onClick={handleUrlNext} disabled={!sourceUrl.trim()}>Next</Button>
                </div>
              </div>
            )
          })()}

          {showDropZone && (
            files.length > 0 ? (
              <div className="rounded-xl border-2 border-foreground/20 bg-secondary/20 p-8 flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-foreground/10 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-base font-semibold">{imageFiles.length} image{imageFiles.length !== 1 ? 's' : ''} ready</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{totalSizeLabel} · {files.length} file{files.length !== 1 ? 's' : ''} selected</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setFiles([]); pickFiles('folder') }}>Change</Button>
                  <Button size="sm" onClick={() => goToStep(2)}>Continue →</Button>
                </div>
                <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileInput} />
                {/* @ts-expect-error webkitdirectory */}
                <input ref={folderRef} type="file" webkitdirectory="" className="hidden" onChange={handleFileInput} />
              </div>
            ) : (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={["rounded-xl border-2 border-dashed p-10 flex flex-col items-center gap-4 transition-colors",
                  dragOver ? "border-foreground/60 bg-secondary/40" : "border-border"].join(" ")}
              >
                <UploadCloud className={["w-10 h-10 transition-colors", dragOver ? "text-foreground" : "text-muted-foreground"].join(" ")} />
                <div className="text-center">
                  <p className="text-sm font-medium">Drop your images here</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Flat folder of product images — all together, no subfolders needed</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={!!collectingWhich} onClick={() => pickFiles('images')}>
                    {collectingWhich === 'images' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading…</> : 'Pick images'}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!!collectingWhich} onClick={() => pickFiles('folder')}>
                    {collectingWhich === 'folder' ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading…</> : 'Pick folder'}
                  </Button>
                </div>
                <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileInput} />
                {/* @ts-expect-error webkitdirectory */}
                <input ref={folderRef} type="file" webkitdirectory="" className="hidden" onChange={handleFileInput} />
              </div>
            )
          )}
        </div>
      )}

      {/* ── Step 2 — Setup ── */}
      {step === 2 && (
        <div className="flex flex-col gap-6">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Setup</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Configure how images are grouped into products</p>
          </div>

          {source === 'local' && (
            <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-secondary/20">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                <HardDrive className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <div className="text-sm font-semibold">{files.length} file{files.length !== 1 ? 's' : ''} selected</div>
                <div className="text-xs text-muted-foreground">{totalImages} valid image{totalImages !== 1 ? 's' : ''} · {totalSizeLabel}</div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium">Images per product</label>
              <p className="text-xs text-muted-foreground">How many photos were taken per product (e.g. front, side, detail…)</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="number" min={1} max={100} value={imgsPerProduct}
                onChange={e => setImgsPerProduct(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24 h-9 rounded-lg border border-border bg-background px-3 text-sm text-center tabular-nums focus:outline-none focus:ring-1 focus:ring-foreground/30"
              />
              <span className="text-sm text-muted-foreground">images per product</span>
            </div>

            {source === 'local' && totalImages > 0 && (
              divisible ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  <span><strong>{estimatedProducts} products</strong> detected · {imgsPerProduct} images each · {totalImages} total</span>
                </div>
              ) : (
                <NotDivisibleHint total={totalImages} perProduct={safeImgsPerProduct} remainder={remainder} />
              )
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
            <Button
              onClick={startUpload}
              disabled={source === 'local' && (!divisible || totalImages === 0)}
            >
              {source === 'local'
                ? (!divisible ? 'Fix image count to continue' : `Upload ${totalImages} images`)
                : 'Start batch'
              }
            </Button>
          </div>
        </div>
      )}

      {/* ── Step 3 — Upload ── */}
      {step === 3 && (
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                {uploading
                  ? (source === 'drive' || source === 'dropbox') ? `Pulling from ${source === 'drive' ? 'Drive' : 'Dropbox'}…` : 'Uploading…'
                  : uploadError ? 'Failed' : 'Ready'}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {uploading
                  ? (source === 'drive' || source === 'dropbox')
                    ? 'Downloading files from cloud…'
                    : `${uploadPct}% — ${Math.round(totalImages * uploadPct / 100)} of ${totalImages} images`
                  : uploadError ? uploadError
                  : pulledCount != null ? `${pulledCount} images pulled and ready`
                  : `${totalImages} images ready`}
                {uploading && pullingFile && (source === 'drive' || source === 'dropbox') && (
                  <span className="block text-xs text-muted-foreground truncate max-w-xs mt-0.5">{pullingFile}</span>
                )}
              </p>
            </div>
            {uploading && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={() => setBackground(true)}>
                    <Minimize2 className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Run in background</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-foreground rounded-full transition-all duration-200" style={{ width: `${uploadPct}%` }} />
            </div>
            <div className="text-xs text-muted-foreground text-right">{uploadPct}%</div>
          </div>
          {uploadError && (
            <Button variant="outline" onClick={() => setStep(2)}>← Back</Button>
          )}
        </div>
      )}

      {/* ── Step 4 — Configure (redirect) ── */}
      {step === 4 && (
        <div className="flex flex-col items-center gap-6 py-10">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <div className="text-center flex flex-col gap-1">
            <h2 className="text-xl font-semibold tracking-tight">Upload complete</h2>
            <p className="text-sm text-muted-foreground">
              {estimatedProducts ? `${estimatedProducts} products · ` : ''}{pulledCount ?? totalImages} images ready
            </p>
          </div>
          <Button size="lg" onClick={() => createdJobId && onConfigure?.(String(createdJobId))}>
            Configure job →
          </Button>
        </div>
      )}
    </div>
  )
}
